import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  db, doc, addDoc, updateDoc, collection, getDoc, serverTimestamp, increment
} from '../firebase/config';
import { X, Star, Check, AlertCircle } from 'lucide-react';

const ReviewModal = ({ booking, onClose, onSuccess }) => {
  const { user, userData } = useAuth();
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  if (!booking || !user) return null;

  const proName = booking.professionalName || 'the professional';
  const serviceName = booking.serviceTitle || booking.serviceName || 'your service';

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setError(null);

    if (rating < 1) {
      setError('Please select a rating');
      return;
    }
    if (!booking.professionalId) {
      setError('Cannot review this booking');
      return;
    }

    setSaving(true);
    try {
      // 1. Create review doc
      const reviewRef = await addDoc(collection(db, 'reviews'), {
        professionalId: booking.professionalId,
        professionalName: booking.professionalName || 'Professional',
        clientId: user.uid,
        clientName: userData?.name || user.displayName || 'Client',
        clientImage: userData?.profileImage || user.photoURL || null,
        bookingId: booking.id,
        serviceId: booking.serviceId || null,
        serviceTitle: booking.serviceTitle || booking.serviceName || null,
        rating: Number(rating),
        text: text.trim(),
        createdAt: serverTimestamp(),
      });

      // 2. Update the booking with the review reference
      await updateDoc(doc(db, 'bookings', booking.id), {
        reviewId: reviewRef.id,
        rating: Number(rating),
        reviewedAt: serverTimestamp(),
      });

      // 3. Aggregate onto the pro's user doc (rating + count)
      const proRef = doc(db, 'users', booking.professionalId);
      const proSnap = await getDoc(proRef);

      if (proSnap.exists()) {
        const data = proSnap.data();
        const currentCount = Number(data.ratingCount || 0);
        const currentRating = Number(data.rating || 0);

        const newCount = currentCount + 1;
        const newRating = ((currentRating * currentCount) + Number(rating)) / newCount;

        await updateDoc(proRef, {
          rating: Math.round(newRating * 10) / 10,
          ratingCount: newCount,
          lastReviewAt: serverTimestamp(),
        });
      }

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error('Review submission failed:', err);
      setError(err.message || 'Failed to submit review');
    } finally {
      setSaving(false);
    }
  };

  const displayRating = hoverRating || rating;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-t-3xl sm:rounded-3xl max-w-md w-full max-h-[92vh] overflow-hidden flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-5 border-b border-gray-50 flex items-center justify-between shrink-0">
          <div>
            <h2 className="font-serif font-bold text-xl text-gray-900">
              Rate your experience
            </h2>
            <p className="text-xs text-gray-400 mt-0.5 truncate">
              {proName} · {serviceName}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-full bg-[#FCF8F9] flex items-center justify-center hover:bg-gray-100 shrink-0"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-5 overflow-y-auto">

          {/* Star rating */}
          <div className="text-center py-3">
            <div className="flex justify-center gap-2 mb-3">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  onMouseEnter={() => setHoverRating(n)}
                  onMouseLeave={() => setHoverRating(0)}
                  onClick={() => setRating(n)}
                  className="transition-transform hover:scale-110"
                >
                  <Star
                    size={40}
                    className={`transition-colors ${
                      n <= displayRating
                        ? 'fill-amber-400 text-amber-400'
                        : 'text-gray-200 fill-gray-100'
                    }`}
                  />
                </button>
              ))}
            </div>
            <p className="text-sm font-bold text-gray-700 h-5">
              {rating === 1 && 'Poor'}
              {rating === 2 && 'Fair'}
              {rating === 3 && 'Good'}
              {rating === 4 && 'Great'}
              {rating === 5 && 'Excellent'}
              {rating === 0 && '\u00A0'}
            </p>
          </div>

          {/* Text */}
          <div>
            <label className="text-[10px] font-bold text-gray-500 uppercase tracking-[0.15em] block mb-1.5">
              Add a note (optional)
            </label>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Tell others what you liked or would change..."
              rows={4}
              maxLength={280}
              className="w-full bg-[#FCF8F9] border border-transparent rounded-2xl px-4 py-3 text-sm focus:outline-none focus:border-[#EFA9B6] focus:bg-white transition-colors resize-none"
            />
            <p className="text-[10px] text-gray-400 text-right mt-1">
              {text.length}/280
            </p>
          </div>

          {/* Info */}
          <div className="bg-[#FFF5F6] rounded-2xl p-3 flex items-start gap-2">
            <AlertCircle size={14} className="text-[#EFA9B6] shrink-0 mt-0.5" />
            <p className="text-[11px] text-gray-600 leading-relaxed">
              Your review will be public on {proName}'s profile and helps other clients choose.
            </p>
          </div>

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-100 rounded-2xl p-3 flex items-start gap-2">
              <AlertCircle size={16} className="text-red-500 shrink-0 mt-0.5" />
              <p className="text-xs text-red-600">{error}</p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-5 pt-4 border-t border-gray-50 shrink-0">
          <button
            type="button"
            onClick={handleSubmit}
            disabled={rating < 1 || saving}
            className="w-full bg-[#010101] text-white py-4 rounded-full font-bold text-sm hover:bg-black/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                Submitting...
              </>
            ) : (
              <>
                <Check size={16} /> Submit Review
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReviewModal;